<template>
  <div>
    <page-nav-bar
      :is-sticky="true"
      :back-button="true"
      :has-actions="false">
      <template slot="breadcrumb">
        <h1 class="page-header-breadcrumb-item is-uppercase">
          <span class="">{{ total }} Adv<span v-if="total > 1">s</span></span>
        </h1>
      </template>
    </page-nav-bar>

    <page-content
      :has-sticky-col-left="true"
      :has-left-col-collapse="true"
      :opened-col="openedCol"
      :has-padd-right-normal="true">
      <template slot="left-col-small">
        <col-collapse-filter
          :has-filter="hasFilter"
          :loading="loading"
          @opened="openedCol = !openedCol"
          @resetFilters="resetFilters()">
          <div class="is-padding">
            <div class="field">
              <div class="control">
                <multiselect
                  v-model="filters.contact"
                  ref="contactMultiselect"
                  label="name"
                  track-by="pk"
                  :options="contacts"
                  :searchable="true"
                  :show-labels="false"
                  class="multiselect--option-ico is-single_choice"
                  :custom-label="val => limitText(`${val.last_name} ${val.first_name}`)"
                  :placeholder="''"
                  :internal-search="false"
                  @select="onContactSelection"
                  @remove="updateFilter([], 'contact')"
                  @search-change="onSearchChange">
                  <div
                    slot="clear"
                    slot-scope="props"
                    class="multiselect__clear"
                    @mousedown.prevent.stop="clearAll(props.search)" />
                  <span slot="noResult">Oups! Aucun acquéreur trouvée.</span>
                  <span slot="noOptions">La liste est vide</span>
                  <span
                    slot="singleLabel"
                    class="multiselect__placeholder">Acquéreur</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Acquéreur</span>
                  <span
                    v-if="!loadingContacts && searchInput.trim()"
                    slot="afterList">
                    <li
                      class="searchList-suggestion multiselect__option">
                      <a
                        class="searchResult-list-item searchResult-list-item--viewMore"
                        @click="onSearchByContactName">
                        <span
                          class="has-text-weight-medium">Voir tout</span>
                      </a>
                    </li>
                  </span>
                </multiselect>
              </div>
            </div>
            <div
              v-if="isAdmin && !isPromoteur"
              class="field">
              <div class="control">
                <multiselect
                  v-model="filters.promoteur"
                  label="name"
                  track-by="pk"
                  :options="promoteurs"
                  :searchable="true"
                  :show-labels="false"
                  class="multiselect--option-ico is-single_choice"
                  :placeholder="''"
                  @select="updateSimpleFilter"
                  @remove="updateFilter([], 'promoteur')"
                  @search-change="getPromoteurs">
                  <template
                    slot="clear"
                    slot-scope="props">
                    <div
                      v-if="promoteurs.length"
                      class="multiselect__clear"
                      @mousedown.prevent.stop="clearAll(props.search)" />
                  </template>
                  <span slot="noResult">Oups! Aucun promoteur trouvée.</span>
                  <span slot="noOptions">La liste est vide</span>
                  <span
                    slot="singleLabel"
                    class="multiselect__placeholder">Promoteur</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Promoteur</span>
                </multiselect>
              </div>
            </div>
            <div
              v-if="isAdmin || activeAllContact"
              class="field">
              <div class="control">
                <multiselect
                  @input="updateSimpleFilter"
                  @search-change="getProgramsByName"
                  @open="!programsFilter.length ? getProgramsByName() : null"
                  :loading="loadingFilters === 'program'"
                  v-model="filters.programs"
                  label="name"
                  track-by="pk"
                  class="multiselect--option-ico"
                  open-direction="bottom"
                  :multiple="true"
                  :options="programsFilter"
                  :searchable="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :show-labels="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :placeholder="''">
                  <template
                    slot="tag"
                    slot-scope="props">
                    <span></span>
                  </template>
                  <template
                    slot="selection"
                    slot-scope="{ values, isOpen }">
                    <span
                      v-if="values.length && !isOpen"
                      class="multiselect__placeholder">
                      Programmes ({{ values.length }})
                    </span>
                  </template>
                  <template slot="beforeList">
                    <li class="multiselect__element">
                      <span
                        @click="toggleAllPrograms"
                        @mouseenter="hoveredAllPrograms = true"
                        @mouseleave="hoveredAllPrograms = false"
                        class="multiselect__option"
                        :class="{
                          'multiselect__option--selected': filters.programs && filters.programs.length === programsFilter.length,
                          'multiselect__option--highlight': hoveredAllPrograms
                        }">
                        <span>Tous les programmes</span>
                      </span>
                    </li>
                  </template>
                  <span slot="noResult">Oups! Aucun programme trouvé.</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Programmes</span>
                  <span slot="noOptions">La liste est vide</span>
                </multiselect>
              </div>
            </div>
            <div class="field">
              <div class="control">
                <multiselect
                  v-model="filters.origin"
                  v-if="origins"
                  track-by="pk"
                  @input="updateSimpleFilter"
                  class="multiselect--option-ico"
                  :custom-label="val => val.name"
                  :value="filters.origin"
                  :close-on-select="false"
                  group-values="children"
                  group-label="name"
                  :group-select="true"
                  :multiple="true"
                  :options="activeOriginGroups"
                  :searchable="true"
                  :show-labels="false"
                  @search-change="getOriginsByName"
                  :placeholder="''">
                  <template
                    slot="clear"
                    slot-scope="props">
                    <div
                      class="multiselect__clear"
                      v-if="origins.length"
                      @mousedown.prevent.stop="clearAll(props.search)"></div>
                  </template>
                  <span slot="noResult">Oups! Aucune origine trouvée.</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Origine(s)</span>
                  <span
                    slot="selection"
                    class="multiselect__placeholder"
                    slot-scope="{ values }"
                    v-if="values.length">
                    <span>Origine(s) ({{ values.length }})</span>
                  </span>
                  <span slot="noOptions">La liste est vide</span>
                </multiselect>
              </div>
            </div>
            <div
              v-if="filters.programs && filters.programs.length"
              class="columns">
              <div class="column is-paddingless-bottom is-padding-top-xsmall">
                <div class="control">
                  <multiselect
                    v-model="filters.lot"
                    class="multiselect--option-ico"
                    label="ref"
                    track-by="pk"
                    :options="lots"
                    :searchable="true"
                    :show-labels="false"
                    :placeholder="''"
                    :custom-label="val => limitText(`${val.ref} (${val.program.name})`)"
                    @select="updateSimpleFilter"
                    @remove="updateFilter([], 'lot')"
                    @search-change="getLots">
                    <template
                      slot="clear"
                      slot-scope="props">
                      <div
                        v-if="lots.length"
                        class="multiselect__clear"
                        @mousedown.prevent.stop="clearAll(props.search)" />
                    </template>
                    <span slot="noResult">Oups! Aucun lot trouvé.</span>
                    <span slot="noOptions">La liste est vide</span>
                    <span
                      slot="singleLabel"
                      class="multiselect__placeholder">Lot</span>
                    <span
                      slot="placeholder"
                      class="multiselect__placeholder">Lot</span>
                  </multiselect>
                </div>
              </div>
            </div>
            <div
              v-if="isAdmin || isManager"
              class="columns">
              <div class="column is-paddingless-bottom is-padding-top-xsmall">
                <div class="control">
                  <multiselect
                    v-model="filters.user"
                    class="multiselect--option-ico"
                    label="ref"
                    track-by="pk"
                    :options="users"
                    :searchable="true"
                    :show-labels="false"
                    :placeholder="''"
                    :custom-label="val => limitText(`${val.last_name} ${val.first_name}`)"
                    @select="updateSimpleFilter"
                    @remove="updateFilter([], 'user')"
                    @search-change="getUsers">
                    <template
                      slot="clear"
                      slot-scope="props">
                      <div
                        v-if="users.length"
                        class="multiselect__clear"
                        @mousedown.prevent.stop="clearAll(props.search)" />
                    </template>
                    <span slot="noResult">Oups! Aucun vendeur trouvé.</span>
                    <span slot="noOptions">La liste est vide</span>
                    <span
                      slot="singleLabel"
                      class="multiselect__placeholder">Vendeur</span>
                    <span
                      slot="placeholder"
                      class="multiselect__placeholder">Vendeur</span>
                  </multiselect>
                </div>
              </div>
            </div>
            <div
              v-if="(isAdmin || isManagerPlus) && isPromoteur"
              class="field">
              <div class="control">
                <multiselect
                  v-model="filters.account"
                  label="name"
                  track-by="pk"
                  :options="accounts"
                  :searchable="true"
                  :show-labels="false"
                  class="multiselect--option-ico is-single_choice"
                  :placeholder="''"
                  @select="updateSimpleFilter"
                  @remove="updateFilter([], 'account')"
                  @search-change="getAccounts">
                  <template
                    slot="clear"
                    slot-scope="props">
                    <div
                      v-if="accounts.length"
                      class="multiselect__clear"
                      @mousedown.prevent.stop="clearAll(props.search)" />
                  </template>
                  <span slot="noResult">Oups! Aucune société trouvée.</span>
                  <span slot="noOptions">La liste est vide</span>
                  <span
                    slot="singleLabel"
                    class="multiselect__placeholder">Société</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Société</span>
                </multiselect>
              </div>
            </div>
            <div
              v-if="(isAdmin && isPromoteur) || currentUser.active_all_contact"
              class="field">
              <div class="control">
                <multiselect
                  @input="updateSimpleFilter"
                  @search-change="getAgenceByName"
                  @open="!programsFilter.length ? getAgenceByName() : null"
                  :loading="loadingFilters === 'agence'"
                  v-model="filters.agences_program"
                  label="name"
                  track-by="pk"
                  class="multiselect--option-ico is-single_choice"
                  open-direction="bottom"
                  :multiple="true"
                  :options="agencesFilter"
                  :searchable="true"
                  :close-on-select="false"
                  :show-labels="false"
                  :placeholder="''">
                  <span slot="noResult">Oups! Aucune agence trouvée.</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Agence programme</span>
                  <span
                    slot="selection"
                    class="multiselect__placeholder"
                    slot-scope="{ values }"
                    v-if="values.length">
                    <span>Agences programme ({{ values.length }})</span>
                  </span>
                  <span slot="noOptions">La liste est vide</span>
                </multiselect>
              </div>
            </div>
            <div
              v-if="(isAdmin && isPromoteur) || currentUser.active_all_contact"
              class="field">
              <div class="control">
                <multiselect
                  @input="updateSimpleFilter"
                  @search-change="getAgenceByName"
                  @open="!programsFilter.length ? getAgenceByName() : null"
                  :loading="loadingFilters === 'agence'"
                  v-model="filters.agence"
                  label="name"
                  track-by="pk"
                  class="multiselect--option-ico is-single_choice"
                  open-direction="bottom"
                  :multiple="true"
                  :options="agencesFilter"
                  :searchable="true"
                  :close-on-select="false"
                  :show-labels="false"
                  :placeholder="''">
                  <span slot="noResult">Oups! Aucune agence trouvée.</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Agence vendeur</span>
                  <span
                    slot="selection"
                    class="multiselect__placeholder"
                    slot-scope="{ values }"
                    v-if="values.length">
                    <span>Agences vendeur ({{ values.length }})</span>
                  </span>
                  <span slot="noOptions">La liste est vide</span>
                </multiselect>
              </div>
            </div>
            <div
              v-if="isAdmin && isPromoteur"
              class="field">
              <div class="control">
                <multiselect
                  @input="updateSimpleFilter"
                  :loading="loadingFilters === 'sales_modes'"
                  v-model="filters.sales_modes"
                  label="label"
                  track-by="label"
                  class="multiselect--option-ico is-single_choice"
                  open-direction="bottom"
                  :multiple="true"
                  :options="salesModeChoices"
                  :searchable="true"
                  :close-on-select="false"
                  :show-labels="false"
                  :placeholder="''">
                  <span slot="noResult">Oups! Aucun mode de vente trouvé.</span>
                  <span
                    slot="placeholder"
                    class="multiselect__placeholder">Mode de vente</span>
                  <span
                    slot="selection"
                    class="multiselect__placeholder"
                    slot-scope="{ values }"
                    v-if="values.length">
                    <span>Mode de vente ({{ values.length }})</span>
                  </span>
                  <span slot="noOptions">La liste est vide</span>
                </multiselect>
              </div>
            </div>
            <!-- <div
              v-if="isAdmin || isManager"
              class="columns">
              <div class="column is-paddingless-bottom is-padding-top-xsmall">
                <div class="control">
                  <multiselect
                    v-model="filters.canceled_choice_type"
                    class="multiselect--option-ico"
                    label="label"
                    track-by="value"
                    :options="cancelChoices"
                    :searchable="true"
                    :show-labels="false"
                    :placeholder="''"
                    :custom-label="val => val.label"
                    @select="updateSimpleFilter"
                    @remove="updateFilter([], 'canceled_choice_type')">
                    <template
                      slot="clear"
                      slot-scope="props">
                      <div
                        v-if="users.length"
                        class="multiselect__clear"
                        @mousedown.prevent.stop="clearAll(props.search)" />
                    </template>
                    <span slot="noResult">Oups! Aucune raison d'annulation trouvé.</span>
                    <span slot="noOptions">La liste est vide</span>
                    <span
                      slot="singleLabel"
                      class="multiselect__placeholder">Raison d'annulation</span>
                    <span
                      slot="placeholder"
                      class="multiselect__placeholder">Raison d'annulation</span>
                  </multiselect>
                </div>
              </div>
            </div> -->
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{signature_date__gte: filters.signature_date__gte, signature_date__lte: filters.signature_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date de réservation'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{sale_date__gte: filters.sale_date__gte, sale_date__lte: filters.sale_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date d\'acte'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{sale_expected_sign_date__gte: filters.sale_expected_sign_date__gte, sale_expected_sign_date__lte: filters.sale_expected_sign_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date d\'acte prévue'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{previous_sign_date__gte: filters.previous_sign_date__gte, previous_sign_date__lte: filters.previous_sign_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date réservation prévue'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{updated_on__gte: filters.updated_on__gte, updated_on__lte: filters.updated_on__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date de modification'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <!-- <div class="field">
              <div class="control">
                <date-picker
                  :model="{degradation_deposit_date__gte: filters.degradation_deposit_date__gte, degradation_deposit_date__lte: filters.degradation_deposit_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date caution de dégradation'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{other_degradation_deposit_date__gte: filters.other_degradation_deposit_date__gte, other_degradation_deposit_date__lte: filters.other_degradation_deposit_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date autre caution de dégradation'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <date-picker
                  :model="{fences_realisation_date_date__gte: filters.fences_realisation_date_date__gte, fences_realisation_date_date__lte: filters.fences_realisation_date_date__lte}"
                  :style-version="2"
                  :is-range="true"
                  :name="'date'"
                  :placeholder="'Date caution prescriptions architecturales'"
                  :config="{mode: 'range'}"
                  @input="Object.assign(filters, $event); updateSimpleFilter()" />
              </div>
            </div> -->
            <ModalMultiselect
              v-if="naturesList && naturesList.length"
              :opened-modal="openedModal"
              :data-array="naturesList"
              :selected-array="filters.nature"
              button-width="108"
              modal-width="239"
              display-name="Nature du bien"
              @open="openedModal = $event"
              @update="updateFilter($event), updateSimpleFilter()" />
            <ModalMultiselect
              v-if="filteredTypesList && filteredTypesList.length"
              :opened-modal="openedModal"
              :data-array="filteredTypesList"
              :selected-array="filters.lot_type"
              button-width="108"
              modal-width="239"
              display-name="Type du bien"
              @open="openedModal = $event"
              @update="updateFilter($event), updateSimpleFilter()" />
          </div>
        </col-collapse-filter>
      </template>
      <list-top-bar
        :counted="count"
        label="adv(s)"
        :has-filtered="hasFilter"
        :display-export="true"
        :child-action-group="parentActionGroup"
        @update:childActionGroup="handleActionGroupUpdate"
        @startBigExport="handleExportAll">
      </list-top-bar>
      <div class="is-border-left is-border-right is-border-bottom">
        <!-- <list-top-bar
          :has-filter="hasFilter"
          :count="count"
          label="adv(s)"
          :has-multiselection="isAdmin && isPromoteur">

          <div
            v-if="isAdmin && isPromoteur && multiselection && selectedItems.length"
            class="column is-paddingless is-border-right">
            <a
              class="page-header-btn is-small is-success"
              @click="exportXlsFromMultiselect(service, 'liste-advs')">
              <span class="icon"><i class="fas fa-file-export" /></span>
              <span>Exporter Advs</span>
            </a>
          </div>
          <div
            class="is-border-left"
            v-if="isAdmin && isPromoteur && !multiselection">
            <a
              @click="ExportAllAdv"
              data-tooltip="Exporter tous les advs"
              class="page-header-btn is-small is-square stock-vue-tooltip tooltip is-tooltip-left is-tooltip-primary">
              <span class="icon"><i class="fas fa-tasks"></i></span>
            </a>
          </div>
        </list-top-bar> -->
        <div class="is-flex is-space-between is-border-bottom has-background-white">
          <div class="is-padding-left is-padding-top-small is-padding-bottom-small">
            <div class="field is-grouped">
              <p
                v-for="status in activationStatus"
                :key="status.value"
                class="control">
                <label
                  class="checkbox checkRadioIsHide"
                  :for="status.value">
                  <input
                    :id="status.value"
                    v-model="filters.status"
                    :name="status.name"
                    type="checkbox"
                    :value="status.value"
                    @click="updateSimpleFilter">
                  <span
                    class="tag"
                    :class="status.color">
                    <span>{{ status.label }}</span><span class="icon"><i
                      class="far"
                      :class="filters.status.includes(status.value) ? 'fa-check-square' : 'fa-square'" /></span>
                  </span>
                </label>
              </p>
            </div>
          </div>
          <div
            class="export_btn_style_adv is-align-items-flex-end is-border-left is-nowrap"
            style="flex: 0 0 auto"
            v-if="isMultiselect && parentActionGroup">
            <a
              @click="selectedItems.length ? (exportXlsFromMultiselect(service, 'list-adv'), cancelSelect()) : ''"
              :style="selectedItems.length ? 'background-color: #8dbb7d; color: white; cursor: pointer' : 'cursor: default; color: #D7D7D7'"
              class="btn_export_adv is-uppercase">
              <span class="icon"><i class="fas fa-file-excel"></i></span><span>Exporter ADV</span>
            </a>
          </div>
        </div>
        <filters-tags
          id="user-tag"
          :has-filter="hasFilter"
          :filters="filters"
          :ignore-tags="['status']"
          @reset="resetFilters"
          @update="updateFromTag($event)" />
        <spinner-round
          v-if="loading"
          :is-loading="loading" />
        <table-controls-vue
          v-if="!loading && items.length"
          :has-sticky="true"
          :no-scroll-direction="true">
          <table class="table is-fullwidth is-striped-v is-first-fixed-medium is-last-fixed-medium has-actionsontd">
            <thead class="has-cells-vcentered">
              <tr class="is-size-6">
                <th
                  class="is-paddingless has-text-centered"
                  style="z-index:2;">
                  <columns-toggles
                    :entries="columnsList"
                    :source="columns"
                    @update="updateColumns" />
                </th>
                <th>
                  <span class="is-padding-xsmall">Statut</span>
                </th>
                <th>
                  <sortable
                    sort-id="ref"
                    :sort="sortBy.ref"
                    label="Réf."
                    @sort="sort('ref', $event)" />
                </th>
                <th>
                  <sortable
                    :sort="sortBy.lot__ref"
                    label="Lot"
                    @sort="sort('lot__ref', $event)" />
                </th>
                <th
                  v-if="showColumn('lot__equivalent')"
                  class="is-nowrap">
                  <span class="is-padding-xsmall">Équivalent Lot</span>
                </th>
                <th
                  v-if="showColumn('program__name')"
                  class="is-nowrap">
                  <sortable
                    sort-id="program__name"
                    :sort="sortBy.program__name"
                    label="Programme"
                    @sort="sort('program__name', $event)" />
                </th>
                <th
                  v-if="showColumn('program__promoteur_company_name')"
                  class="is-nowrap">
                  <span class="is-padding-xsmall">Société Opération</span>
                </th>
                <th
                  v-if="showColumn('previous_sign_date')"
                  class="is-nowrap">
                  <sortable
                    sort-id="previous_sign_date"
                    :sort="sortBy.previous_sign_date"
                    label="Résa prévue"
                    @sort="sort('previous_sign_date', $event)" />
                </th>
                <th
                  v-if="showColumn('signature_date')"
                  class="is-nowrap">
                  <sortable
                    sort-id="signature_date"
                    :sort="sortBy.signature_date"
                    label="Date résa"
                    @sort="sort('signature_date', $event)" />
                </th>
                <th
                  v-if="showColumn('deadline_date')"
                  class="is-nowrap">
                  <span class="is-padding-xsmall">Date butoir acte</span>
                </th>
                <th
                  v-if="showColumn('account__name') && isAdmin && isPromoteur"
                  class="is-nowrap">
                  <sortable
                    sort-id="account__name"
                    :sort="sortBy.account__name"
                    label="Société vendeur"
                    @sort="sort('account__name', $event)" />
                </th>
                <th v-if="showColumn('promoteur__name') && isAdmin && !isPromoteur">
                  <sortable
                    sort-id="promoteur__name"
                    :sort="sortBy.promoteur__name"
                    label="Promoteur"
                    @sort="sort('promoteur__name', $event)" />
                </th>
                <th v-if="showColumn('user__last_name') && (isAdmin || isManager)">
                  <sortable
                    sort-id="user__last_name"
                    :sort="sortBy.user__last_name"
                    label="Vendeur"
                    @sort="sort('user__last_name', $event)" />
                </th>
                <th
                  v-if="showColumn('contact__last_name')"
                  class="is-nowrap">
                  <sortable
                    sort-id="contact__last_name"
                    :sort="sortBy.contact__last_name"
                    label="Acquéreur"
                    @sort="sort('contact__last_name', $event)" />
                </th>
                <th
                  v-if="showColumn('co_contact__last_name')"
                  class="is-nowrap">
                  <span class="is-padding-xsmall">Co-acquéreur</span>
                </th>
                <th
                  v-if="showColumn('origin__name')"
                  class="is-groupcols-1a">
                  <sortable
                    @sort="sort('origin__name', $event)"
                    sort-id="origin__name"
                    :sort="sortBy.origin__name"
                    label="Origine"></sortable>
                </th>
                <th
                  v-if="showColumn('lot_sale_price_ttc')"
                  class="is-nowrap">
                  <sortable
                    sort-id="lot_sale_price_ttc"
                    :sort="sortBy.lot_sale_price_ttc"
                    label="Prix initial TTC"
                    @sort="sort('lot_sale_price_ttc', $event)" />
                </th>
                <th
                  v-if="showColumn('sale_price_ttc')"
                  class="is-nowrap">
                  <sortable
                    sort-id="sale_price_ttc"
                    :sort="sortBy.sale_price_ttc"
                    label="Prix vente TTC"
                    @sort="sort('sale_price_ttc', $event)" />
                </th>
                <!-- mode de vente  -->
                <th
                  v-if="showColumn('sales_mode')"
                  class="is-nowrap">
                  <span class="is-padding-xsmall">Mode de vente</span>
                </th>
                <th
                  v-if="showColumn('sale_expected_sign_date')"
                  class="is-nowrap">
                  <sortable
                    sort-id="sale_expected_sign_date "
                    :sort="sortBy.sale_expected_sign_date"
                    label="Acte prévu"
                    @sort="sort('sale_expected_sign_date', $event)" />
                </th>
                <th
                  v-if="showColumn('sale_date')"
                  class="is-nowrap">
                  <sortable
                    sort-id="sale_date"
                    :sort="sortBy.sale_date"
                    label="Date d'acte"
                    @sort="sort('sale_date', $event)" />
                </th>
                <th v-if="showColumn('destination')">
                  <sortable
                    sort-id="destination"
                    :sort="sortBy.destination"
                    label="Destination"
                    @sort="sort('destination', $event)" />
                </th>
                <th
                  v-if="showColumn('mandat_de_gestion')"
                  class="is-nowrap">
                  <sortable
                    sort-id="mandat_de_gestion"
                    :sort="sortBy.mandat_de_gestion"
                    label="Mandat de gestion"
                    @sort="sort('mandat_de_gestion', $event)" />
                </th>
                <th
                  v-if="showColumn('sale_loans_edition')"
                  class="is-nowrap">
                  <sortable
                    sort-id="sale_loans_edition"
                    :sort="sortBy.sale_loans_edition"
                    label="Offre de prêt éditée"
                    @sort="sort('sale_loans_edition', $event)" />
                </th>
                <th v-if="showColumn('pre_reservation_date')">
                  <sortable
                    sort-id="pre_reservation_date"
                    :sort="sortBy.pre_reservation_date"
                    label="Enregistrement"
                    @sort="sort('pre_reservation_date', $event)" />
                </th>
                <th v-if="showColumn('updated_on')">
                  <sortable
                    sort-id="updated_on"
                    :sort="sortBy.updated_on"
                    label="Modification"
                    @sort="sort('updated_on', $event)" />
                </th>
                <th v-if="showColumn('canceled_date')">
                  <sortable
                    sort-id="canceled_date"
                    :sort="sortBy.canceled_date"
                    label="Date d'annulation"
                    @sort="sort('canceled_date', $event)" />
                </th>
                <th
                  v-if="showColumn('loan_application_deadline')"
                  class="is-nowrap">
                  <sortable
                    sort-id="loan_application_deadline"
                    :sort="sortBy.loan_application_deadline"
                    label="Date butoir demande de prêt" />
                </th>
                <th
                  v-if="showColumn('depose_date_loan_offer')"
                  class="is-nowrap">
                  <sortable
                    sort-id="depose_date_loan_offer"
                    :sort="sortBy.depose_date_loan_offer"
                    label="Date d'accord de prêt" />
                </th>
                <th
                  v-if="showColumn('use_loan')"
                  class="is-nowrap">
                  <sortable
                    sort-id="funding_data.use_loan"
                    :sort="sortBy['funding_data.use_loan']"
                    label="Paiement comptant"
                    @sort="sort('funding_data.use_loan', $event)" />
                </th>
                <th class="has-text-centered" />
              </tr>
            </thead>
            <tbody class="has-cells-vcentered">
              <tr>
                <td
                  v-if="multiselection"
                  colspan="21"
                  style="padding: 0">
                  <div
                    class="is-flex is-nowrap is-align-items-center container_select_multiple_adv">
                    <a
                      :style="!selectedItems.length > 0? 'background-color: #219CED' : 'background-color: #8CBB7D'"
                      class="select_multiple_adv"
                      @click="$store.commit('selectAllMultiselectionItems')">
                      <i
                        style="font-size: 17px; padding: 0px 22px 0px 16px"
                        class="far fa-check-double"></i>
                      <span class="is-padding-left-xxsmall">{{ !selectedItems.length > 0 ? 'Cliquez ici pour sélectionner toutes les lignes de la page' : 'Déselectionnez toutes les lignes de la page' }}</span>
                    </a>
                  </div>
                </td>
              </tr>
              <tr
                v-for="(adv) in items"
                :id="'e' + adv.pk"
                :key="adv.pk"
                class="is-size-6"
                :class="{'has-status-bar--left has-status-bar--draft': lastOpenedItemPk === adv.pk}">
                <td
                  v-if="(isAdmin && isPromoteur) || currentUser.active_all_contact"
                  @click="openComment(adv)"
                  class="has-cursor-pointer is-cell-hoverable">
                  <div
                    class="is-flex-center has-background-white comment-icon"
                    v-if="adv.comment">
                    <span
                      style="border-color: #FFFFFF;"
                      class="orange-bullet"></span>
                    <span
                      class="icon is-size-5 has-text-grey icon-margin">
                      <i class="fas fa-sticky-note"></i>
                    </span>
                  </div>
                  <div
                    class="is-flex-center has-background-white comment-icon"
                    v-else>
                    <span
                      class="icon is-size-5 has-text-grey icon-margin">
                      <i class="fal fa-sticky-note"></i>
                    </span>
                  </div>
                </td>
                <td v-else></td>
                <td class="has-text-centered">
                  <multiselect-button
                    v-if="multiselection"
                    :data="adv" />
                  <span
                    v-else
                    :data-tooltip="advStatus[adv.status].label"
                    class="dot tooltip is-tooltip-right"
                    :class="adv.status | dotClass" />
                </td>
                <td class="is-cell-hoverable">
                  <router-link
                    :to="{name: 'AdvInfos', params: {id: adv.pk}, query: {returnId: adv.pk}}"
                    class="fullLink">
                    {{ adv.ref }}
                  </router-link>
                </td>
                <td
                  v-if="adv.lot"
                  class="is-cell-hoverable">
                  <router-link
                    :to="{name: 'LotVue', params: {id: adv.lot.pk}}"
                    class="fullLink">
                    {{ adv.lot.ref }}
                  </router-link>
                </td>
                <td v-else>
                  ----
                </td>
                <td
                  v-if="showColumn('lot__equivalent')"
                  class="is-cell-hoverable">
                  <span v-if="adv.lot && adv.lot.equivalent">
                    {{ adv.lot.equivalent }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td
                  v-if="adv.program && showColumn('program__name')"
                  class="is-cell-hoverable">
                  <router-link
                    :to="{name: 'ProgramVue', params: {id: adv.program.pk}}"
                    class="fullLink">
                    {{ adv.program.name }}
                  </router-link>
                </td>
                <td
                  v-if="showColumn('program__promoteur_company_name')"
                  class="is-cell-hoverable">
                  <span v-if="adv.program && adv.program.promoteur_data && adv.program.promoteur_data.company_name">
                    {{ adv.program.promoteur_data.company_name }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td
                  v-if="showColumn('previous_sign_date')"
                  class="is-cell-hoverable">
                  <span v-if="adv.previous_sign_date">
                    {{ adv.previous_sign_date | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>N/A</span>
                </td>
                <td
                  v-if="showColumn('signature_date')"
                  class="is-cell-hoverable">
                  <span v-if="adv.signature_date">
                    {{ adv.signature_date | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>N/A</span>
                </td>
                <template v-if="showColumn('deadline_date')">
                  <td class="is-cell-hoverable">
                    <template v-for="(sru, idx) in adv.signature_data.sru_list">
                      <span :key="idx">
                        <span v-if="sru.deadline_date">
                          {{ sru.deadline_date | dtFormat('DD/MM/YYYY') }}
                        </span>
                        <span v-else>--</span>
                        <span v-if="idx < adv.signature_data.sru_list.length - 1">, </span>
                      </span>
                    </template>
                  </td>
                </template>
                <td
                  v-if="showColumn('account__name') && isAdmin && isPromoteur && adv.account"
                  class="is-cell-hoverable">
                  <span class="fullLink">
                    {{ adv.account.name }}
                  </span>
                </td>
                <td
                  v-else-if="showColumn('account__name') && isAdmin && isPromoteur && adv.seller_data.account"
                  class="is-cell-hoverable">
                  <span class="fullLink">
                    {{ adv.seller_data.account.name }}
                  </span>
                </td>
                <!-- && isPromoteurDetailsActivated(adv) -->
                <!-- <td
                  v-if="showColumn('promoteur__name') && isAdmin && !isPromoteur"
                  class="is-cell-hoverable">
                  <router-link
                    class="fullLink"
                    :to="{name: 'PromoteurPresentation', params: {id: adv.promoteur.pk}}">
                    {{ adv.promoteur.name }}
                  </router-link>
                </td> -->
                <!-- && !isPromoteurDetailsActivated(adv) -->
                <td v-if="showColumn('promoteur__name') && isAdmin && !isPromoteur">
                  {{ adv.promoteur.name }}
                </td>
                <td
                  v-if="showColumn('user__last_name') && (isAdmin || isManager) && adv.seller_data"
                  :class="{'is-cell-hoverable' : !adv.seller_data.deleted}">
                  <router-link
                    v-if="!adv.seller_data.deleted"
                    :to="{name: 'UserVue', params: {id: adv.seller_data.pk}}"
                    class="fullLink">
                    {{ adv.seller_data.last_name }} {{ adv.seller_data.first_name }}
                  </router-link>
                  <span v-else>
                    {{ adv.seller_data.last_name }} {{ adv.seller_data.first_name }}
                  </span>
                </td>
                <td
                  v-if="showColumn('contact__last_name')"
                  class="is-cell-hoverable"
                  style="white-space: nowrap;">
                  <router-link
                    v-if="adv.buyers_data[0] && adv.buyers_data[0].account.pk === currentUser.account.pk && adv.buyers_data[0].nature == 'corporation'"
                    :to="{name: 'ContactVue', params: {id: adv.buyers_data[0].pk}}"
                    class="fullLink">
                    <span v-if="adv.buyers_data[0].corporation_name">{{ adv.buyers_data[0].corporation_name }} </span>{{ adv.buyers_data[0].last_name }} {{ adv.buyers_data[0].first_name }}
                  </router-link>
                  <router-link
                    v-else-if="adv.buyers_data[0] && adv.buyers_data[0].account.pk === currentUser.account.pk && adv.buyers_data[0].nature !== 'corporation'"
                    :to="{name: 'ContactVue', params: {id: adv.buyers_data[0].pk}}"
                    class="fullLink">
                    {{ adv.buyers_data[0].last_name }} {{ adv.buyers_data[0].first_name }}
                  </router-link>
                  <template v-else-if="adv.buyers_data[0] && adv.buyers_data[0].nature !== 'corporation'">
                    {{ adv.buyers_data[0].last_name }} {{ adv.buyers_data[0].first_name }}
                  </template>
                  <template v-else-if="adv.buyers_data[0] && adv.buyers_data[0].nature == 'corporation'">
                    <span v-if="adv.buyers_data[0].corporation_name">{{ adv.buyers_data[0].corporation_name }} </span>{{ adv.buyers_data[0].last_name }} {{ adv.buyers_data[0].first_name }}
                  </template>
                </td>
                <td
                  v-if="showColumn('co_contact__last_name')"
                  class="is-cell-hoverable is-narrow is-truncate">
                  <template v-if="adv.buyers_data.length > 1">
                    <div
                      v-for="(buyer, index) in adv.buyers_data"
                      :key="index">
                      <!-- <template v-if="index > 0 && buyer.first_name !== adv.contact.first_name"> -->
                      <template v-if="index > 0 && buyer && buyer.first_name && adv.contact && adv.contact.first_name && (buyer.first_name !== adv.contact.first_name)">
                        <span>{{ buyer.first_name }} {{ buyer.last_name }}<span v-if="adv.buyers_data.length > 2 && index < 2">,</span></span>
                      </template>
                    </div>
                  </template>
                  <span v-else>--</span>
                </td>
                <td
                  v-if="showColumn('origin__name')"
                  class="is-cell-hoverable is-narrow is-truncate">
                  <span
                    v-if="isPromoteur && adv.buyers_data[0].origin && (currentUser.account.pk === adv.buyers_data[0].account.pk)"
                    :title="adv.buyers_data[0].origin.name">
                    {{ adv.buyers_data[0].origin.name }}
                  </span>
                  <span
                    v-else-if="isDistributeur && adv.buyers_data[0].origin && (adv.contact.shared === true || adv.buyers_data[0].account.pk === currentUser.account.pk)"
                    :title="adv.buyers_data[0].origin.name">
                    {{ adv.buyers_data[0].origin.name }}
                  </span>
                  <span v-else>-</span>
                </td>
                <!-- prix total ttc -->
                <td v-if="showColumn('lot_sale_price_ttc')">
                  <span v-if="adv.lot_data.sale_price_ttc">
                    {{ adv.lot_data.sale_price_ttc | prettyPrice }} €
                  </span>
                  <span v-else>--</span>
                </td>
                <!-- prix total ttc -->
                <td v-if="showColumn('sale_price_ttc')">
                  <span v-if="adv.sale_price_ttc">
                    {{ adv.sale_price_ttc | prettyPrice }} €
                  </span>
                  <span v-else>--</span>
                </td>
                <!-- mode de vente -->
                <td v-if="showColumn('sales_mode')">
                  <span v-if="adv.lot.sales_mode">
                    {{ adv.lot.sales_mode | salesmodeLabel }}
                  </span>
                  <span v-else>--</span>
                </td>
                <!-- date previsionnelle -->
                <td v-if="showColumn('sale_expected_sign_date')">
                  <span v-if="adv.sale_expected_sign_date">
                    {{ adv.sale_expected_sign_date | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('sale_date')">
                  <span v-if="adv.sale_date">
                    {{ adv.sale_date | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td
                  v-if="showColumn('destination')"
                  class="is-cell-hoverable">
                  <span
                    class="fullLink"
                    v-if="adv.destination">
                    {{ adv.destination | contactResearchLabel }}
                  </span>
                  <span v-else>NC</span>
                </td>
                <td v-if="showColumn('mandat_de_gestion')">
                  <span v-if="adv.destination === 'invest' && adv.mandat_de_gestion">Oui</span>
                  <span v-else-if="adv.destination === 'invest' && !adv.mandat_de_gestion">Non</span>
                  <span v-else-if="adv.destination === 'invest'">NC</span>
                  <span v-else />
                </td>
                <!-- offre de prêt éditée le -->
                <td v-if="showColumn('sale_loans_edition')">
                  <span v-if="adv.sale_loans_edition">
                    {{ adv.sale_loans_edition | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('pre_reservation_date')">
                  <span v-if="adv.pre_reservation_date">
                    {{ adv.pre_reservation_date | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('updated_on')">
                  <span v-if="adv.updated_on">
                    {{ adv.updated_on | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('canceled_date')">
                  <span v-if="adv.updated_on && adv.status === 'canceled'">
                    {{ adv.updated_on | dtFormat('DD/MM/YYYY') }}
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('loan_application_deadline')">
                  <span v-if="adv.sale_loans">
                    <span
                      v-for="(loan, index) in adv.sale_loans"
                      :key="index">
                      <span v-if="loan.loan_application_deadline !== null">{{ loan.loan_application_deadline | dtFormat('DD/MM/YYYY') }}</span>
                      <span v-else>--</span>
                    </span>
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('depose_date_loan_offer')">
                  <span v-if="adv.sale_loans">
                    <span
                      v-for="(loan, index) in adv.sale_loans"
                      :key="index">
                      <span v-if="loan.depose_date_loan_offer !== null">{{ loan.depose_date_loan_offer | dtFormat('DD/MM/YYYY') }}</span>
                      <span v-else>--</span>
                    </span>
                  </span>
                  <span v-else>--</span>
                </td>
                <td v-if="showColumn('use_loan')">
                  <span v-if="adv.funding_data.use_loan === false">
                    <span>Oui</span>
                  </span>
                  <span v-else>--</span>
                </td>
                <td class="has-text-centered">
                  <display-button-list
                    :buttons="buttonList"
                    :data="adv"
                    :hide-text-when-solo="true" />
                </td>
              </tr>
            </tbody>
          </table>
        </table-controls-vue>
        <div v-show="!loading && !items.length">
          <div class="is-padding">
            <h2 class="title is-3 has-text-weight-bold">
              Aucune adv disponible
            </h2>
            <a
              v-if="hasFilter"
              class="has-text-primary"
              @click="resetFilters"><span class="icon"><i
                class="fas fa-undo-alt" /></span> <span>Réinitialiser les filtres</span></a>
          </div>
        </div>
        <pagination
          :loading="loading"
          :current-page="currentPage"
          :total-page="totalPage"
          @changePage="loadItems($event)" />
      </div>
    </page-content>
  </div>
</template>

<script>
import { CANCEL_ADV_CHOICES, LOT_SALES_MODE } from '@/mixins/DefaultValues'
import moment from 'moment'
import { ColumnConf, DefaultValues, Filter, FilterGetter, GenericList, Multiselection, Sort, Quickview } from '@/mixins'
import { mapGetters, mapState } from 'vuex'
import { cloneDeep } from 'lodash'
import ModalMultiselect from '../utils/form/ModalMultiselect.vue'
import StockFiltersMixin from '../stock/StockFiltersMixin'
import UploadMixin from '../../mixins/UploadMixin'

export default {
  name: 'AdvList',
  components: [ ModalMultiselect ],
  filters: {
    dotClass (value) {
      const COLORS = {
        'approved': 'has-background-success',
        'waiting_list': 'has-background-warning',
        'expired': 'has-background-draft',
        'pre_reserved': 'has-background-info',
        'reserved': 'has-background-reserved',
        'sold': 'has-background-sold',
        'canceled': 'has-background-danger',
        'canceled_auto': 'has-background-danger'
      }
      return COLORS[value]
    }
  },
  mixins: [GenericList,
    Filter,
    Sort,
    ColumnConf,
    DefaultValues,
    FilterGetter,
    Multiselection,
    Quickview,
    StockFiltersMixin,
    UploadMixin
  ],
  data () {
    return {
      loadDocuments: this.loadItems,
      setQueryAtStart: true,
      loading: false,
      loadingContacts: false,
      columnsConfResource: 'adv',
      moment: moment(),
      searchInput: '',
      searchInputTimeout: null,
      hoveredAllPrograms: false,
      filters: {
        status: ['sold', 'reserved', 'pre_reserved'],
        destination: [],
        mandat_de_gestion: null,
        origin: [],
        pole: [],
        account: null,
        contact: null,
        signature_date__gte: null,
        signature_date__lte: null,
        sale_date__gte: null,
        sale_date__lte: null,
        programs: [],
        user: null,
        lot: null,
        canceled_choice_type: null,
        degradation_deposit_date__gte: null,
        degradation_deposit_date__lte: null,
        other_degradation_deposit_date__gte: null,
        other_degradation_deposit_date__lte: null,
        fences_realisation_date_date__gte: null,
        fences_realisation_date__lte: null,
        sale_expected_sign_date__gte: null,
        sale_expected_sign_date__lte: null,
        updated_on__gte: null,
        updated_on__lte: null,
        distance: null,
        location: null,
        searchable_contact: null,
        agence: []
      },
      initFilters: {
        status: ['sold', 'reserved', 'pre_reserved'],
        destination: [],
        mandat_de_gestion: null,
        origin: [],
        pole: [],
        account: null,
        contact: null,
        signature_date__gte: null,
        signature_date__lte: null,
        sale_date__gte: null,
        sale_date__lte: null,
        programs: [],
        user: null,
        lot: null,
        canceled_choice_type: null,
        degradation_deposit_date__gte: null,
        degradation_deposit_date__lte: null,
        other_degradation_deposit_date__gte: null,
        other_degradation_deposit_date__lte: null,
        fences_realisation_date_date__gte: null,
        fences_realisation_date__lte: null,
        sale_expected_sign_date__gte: null,
        sale_expected_sign_date__lte: null,
        updated_on__gte: null,
        updated_on__lte: null,
        distance: null,
        location: null,
        agence: []
      },
      buttonList: [
        {
          icon: 'fa-eye',
          text: 'Voir',
          display: (adv) => {
            return true
          },
          action: (advPk) => {
            this.$router.push({ name: 'AdvInfos', params: { id: advPk }, query: { returnId: advPk } })
          }
        }
      ],
      activationStatus: [
        { value: 'pre_reserved', label: 'Pré-Réservation', color: 'is-info' },
        { value: 'reserved', label: 'Réservation', color: 'is-reserved' },
        { value: 'sold', label: 'Acté', color: 'is-sold' },
        { value: 'canceled', label: 'Annulé', color: 'is-danger' }
      ],
      items: [],
      service: 'RESERVATION',
      contacts: [],
      users: [],
      lots: [],
      accounts: [],
      promoteurs: [],
      programs: [],
      openedCol: false,
      openedModal: '',
      advLoading: false,
      advForExport: [],
      parentActionGroup: false,
      origins: [],
      loansData: {}
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isManager', 'isPromoteur', 'activeOriginGroups', 'isDistributeur', 'isManagerPlus']),
    ...mapState({
      savedFilters: 'savedFilters',
      accountsList: 'accounts',
      currentUser: 'currentUser',
      lastOpenedItemPk: 'lastOpenedItemPk',
      quickview: 'quickview'
    }),
    /**
       * today
       * @returns {date}
       */
    today () {
      return moment()
    },
    cancelChoices () {
      return Object.values(CANCEL_ADV_CHOICES)
    },
    salesModeChoices () {
      return Object.values(LOT_SALES_MODE)
    },
    /**
       * columns Show
       * @returns {object}
       */
    columnsShow () {
      const columns = {
        lot__equivalent: { label: 'Équivalent Lot', mobile: true },
        program__name: { label: 'Programme', mobile: true },
        program__promoteur_company_name: { label: 'Société Opération', mobile: true },
        signature_date: { label: 'Réservation', mobile: true },
        previous_sign_date: { label: 'Réservation prévue', mobile: true },
        deadline_date: { label: 'Date butoir acte', mobile: false }
      }
      if (this.isAdmin && this.isPromoteur) {
        columns.account__name = { label: 'Société vendeur', mobile: false }
        // columns.program__name = { label: 'Programme', mobile: true }
      }
      if (this.isAdmin && !this.isPromoteur) {
        columns.promoteur__name = { label: 'Promoteur', mobile: false }
      }
      if (this.isAdmin || this.isManager) {
        columns.user__last_name = { label: 'Vendeur', mobile: true }
      }
      columns.sale_loans_edition = { label: 'Offre de prêt éditée le', mobile: true }
      columns.sale_expected_sign_date = { label: 'Acte prévu', mobile: true }
      columns.contact__last_name = { label: 'Acquéreur', mobile: true }
      columns.co_contact__last_name = { label: 'CoAcquéreur', mobile: true }
      columns.sale_date = { label: 'Acte', mobile: true }
      columns.destination = { label: 'Destination', mobile: false }
      columns.mandat_de_gestion = { label: 'Mandat de gestion', mobile: false }
      columns.pre_reservation_date = { label: 'Enregistrement', mobile: false }
      columns.updated_on = { label: 'Modification', mobile: false }
      columns.sale_price_ttc = { label: 'Prix vente TTC', mobile: true }
      columns.lot_sale_price_ttc = { label: 'Prix initial TTC', mobile: true }
      columns.sales_mode = { label: 'Mode de vente', mobile: true }
      columns.canceled_date = { label: 'Date d\'annulation', mobile: true }
      columns.origin__name = { label: 'Origine', mobile: true }
      columns.loan_application_deadline = { label: 'Date demande de prêt', mobile: true }
      columns.depose_date_loan_offer = { label: 'Date d\'accord de prêt', mobile: true }
      columns.use_loan = { label: 'Paiement comptant', mobile: true }
      return columns
    },
    activeAllContact () {
      return this.currentUser.active_all_contact
    },
    filteredTypesList () {
      // return this.typesList
      return this.typesList.filter((x) => this.filters.nature.some(r => x.show.includes(r)))
    },
    filteredSubtypesList () {
      // return this.subtypesList
      return this.subtypesList.filter((x) => this.filters.nature.some(r => x.show.includes(r)))
    },
    isMultiselect () {
      return this.multiselection
    }
  },
  watch: {
    quickview ({ mode, params, results }) {
      if (!mode && !params && !results) {
        this.initWidget()
        this.loadDocuments()
      }
    },
    'filters.programs' (v) {
      if (v && v.length) {
        this.getLots()
      }
    },
    'filters.nature' (to) {
      // delete type if nature is not selected
      let enabledTypes = this.typesList.filter((x) => to.some(r => x.show.includes(r))).map(x => x.value)
      this.filters.lot_type = this.filters.lot_type.filter(type => enabledTypes.includes(type))
      // delete subtype if nature is not selected
      let enabledSubtypes = this.subtypesList.filter((x) => to.some(r => x.show.includes(r))).map(x => x.value)
      this.filters.lot_subtype = this.filters.lot_subtype.filter(subtype => enabledSubtypes.includes(subtype))
      // delete nb face if nature is not selected
      if (!to.includes('field')) {
        this.filters.nb_face = []
        this.filters.sales_mode = []
      }
      // conditionning display floor button
      if (['flat', 'house', 'commercial', 'office', 'annexe', 'bedroom'].some(x => to.some(nature => x === nature))) {
        this.filteredFloorsList = this.floorsList
      } else {
        this.filteredFloorsList = []
        this.filters.floor = []
      }
    }
  },
  async created () {
    this.cancelSelect()
    if ('advList' in this.savedFilters) {
      this.loadOnCreate = false
      this.initSavedFilters()
    } else if ('status' in this.$route.query && this.$route.query.status) {
      this.filters.status = this.$route.query.status.isArray ? this.$route.query.status : [this.$route.query.status]
    }
    await this.initWidget()
    this.getOrigins()
  },
  mounted () {
    if ('advList' in this.savedFilters) {
      this.loadOnCreate = false
      this.initSavedFilters()
    }
  },
  methods: {
    openComment (adv) {
      let note = {
        message: adv.comment,
        type: 'advComment'
      }
      this.openNoteQuickview({ item: note }, adv.pk)
    },
    async getOrigins () {
      try {
        const { data } = await this.$api.get(this.$resources.ORIGIN, { sort: 'name:asc' })
        this.origins = data.items
      } catch (e) {
        console.log(e)
      }
    },
    initSavedFilters () {
      if (this.savedFilters && this.savedFilters.advList) {
        this.filters = cloneDeep(this.savedFilters.advList)
        if ('status' in this.$route.query && this.$route.query.status) {
          this.filters.status = this.$route.query.status.isArray ? this.$route.query.status : [this.$route.query.status]
        }
        this.$nextTick(() => {
          this.updateSimpleFilter()
        })
      }
    },
    // isPromoteurDetailsActivated (adv) {
    //   return this.accountsList.find(a => a.name === adv.promoteur.name).promoteur_details_activated
    // },
    /**
       * init widget
       */
    async initWidget () {
      await this.getContacts()
      if (!this.isPromoteur && (this.isAdmin || this.isManagerPlus)) {
        this.getPromoteurs()
      }
      if (this.isPromoteur && (this.isAdmin || this.isManagerPlus)) {
        this.getAccounts()
      }
      if (this.isAdmin || this.currentUser.active_all_contact) {
        this.getUsers()
        this.getPrograms()
      }
    },
    /**
       * get contacts
       * @param {string} query
       */
    async getContacts (query) {
      await this.$nextTick()
      this.loadingContacts = true
      let params = {
        searchable_name: query
      }
      try {
        let { data } = await this.$api.get(this.$resources.CONTACT, params)
        this.contacts = data.items.sort((a, b) => a.last_name.localeCompare(b.last_name))
      } catch (err) {
        console.log(err)
      }
      await this.$nextTick()
      this.loadingContacts = false
    },
    /**
       * get program
       * @param {string} query
       */
    async getPrograms (query) {
      let params = {
        sort: 'name:asc',
        searchable_name: query
      }
      try {
        let { data } = await this.$api.get(this.$resources.PROGRAM, params)
        this.programs = data.items
      } catch (err) {
        console.log(err)
      }
    },
    /**
       * get promoteur
       * @param {string} query
       */
    async getPromoteurs (query) {
      let params = {
        status: 'accepted',
        searchable_name: query
      }
      try {
        let { data } = await this.$api.get(this.$resources.ACCOUNT_RELATION, params)
        this.promoteurs = data.items.map(x => x.account_one)
      } catch (err) {
        console.log(err)
      }
    },
    /**
       * get lots
       * @param {string} query
       */
    async getLots (query) {
      let params = {
        searchable_name: query
      }
      if (this.filters.programs && this.filters.programs.length) {
        params.program = this.filters.programs.map(p => p.pk)
      }
      try {
        const { data } = await this.$api.get(this.$resources.LOT, params)
        this.lots = data.items
      } catch (err) {
        console.log(err)
      }
    },
    /**
       * get users
       * @param {string} query
       */
    async getUsers (query) {
      let params = {
        searchable_name: query
      }
      try {
        const { data } = await this.$api.get(this.$resources.USER, params)
        this.users = data.items
      } catch (err) {
        console.log(err)
      }
    },
    /**
       * get accounts
       * @param {string} query
       */
    async getAccounts (query) {
      let params = {
        status: 'accepted',
        searchable_name: query
      }
      try {
        const { data } = await this.$api.get(this.$resources.ACCOUNT_RELATION, params)
        this.accounts = data.items.map(x => x.account_two).sort((a, b) => a.name.localeCompare(b.name))
      } catch (err) {
        console.log(err)
      }
    },
    /**
       * process before get
       * set each query element to pk
       */
    beforeGet () {
      for (let key in this.filters) {
        this.$store.commit('setSavedFilters', { from: 'advList', name: key, value: this.filters[key] })
      }

      if (this.filters.contact) {
        this.query.contact = this.filters.contact.pk
      }
      if (this.filters.programs && this.filters.programs.length) {
        this.query.program = this.filters.programs.map(p => p.pk)
      }
      if (this.filters.lot) {
        this.query.lot = this.filters.lot.pk
      }
      if (this.filters.promoteur) {
        this.query.promoteur = this.filters.promoteur.pk
      }
      if (this.filters.account) {
        this.query.account = this.filters.account.pk
      }
      if (this.filters.user) {
        this.query.user = this.filters.user.pk
      }
    },
    updateFilter ({ filter, array }) {
      if (!array) return
      // check if filter is date_livraison
      if (Array.isArray(filter.value)) {
        filter.value.forEach(year => {
          let i = array.findIndex(x => x === year)
          if (i >= 0) array.splice(i, 1)
          else array.push(year)
        })
      } else {
        let i = array.findIndex(x => x === filter.value)
        if (i >= 0) array.splice(i, 1)
        else array.push(filter.value)
      }
    },
    cancelSelect () {
      this.parentActionGroup = false
      this.$store.commit('cancelMultiselection')
    },
    handleActionGroupUpdate (newValue) {
      this.parentActionGroup = newValue
    },
    async downloadAdvs (list, documentName, service, filters = null) {
      if (!filters) {
        filters = []
      }
      console.log('downloadAdvs', this.items.length)
      const listItems = []
      for (let item of this.items) {
        listItems.push(item.pk)
      }
      try {
        const listItems = []
        for (let item of this.items) {
          listItems.push(item.pk)
        }
        if (this.items.length >= 50) {
          this.$toasted.global.success('Vous allez recevoir une notification quand votre fichier sera prêt')
          const { data } = await this.$api.exportXls(this.$resources[service], { pk: this.selectedItems, ...filters })
          if (data) {
            const zipName = documentName
            const formData = new FormData()
            formData.append('file', data, zipName)
          }
        } else {
          this.$store.commit('setSelectedItems', listItems)
          this.exportXlsFromMultiselect(this.service, 'liste-advs')
        }
      } catch (e) {
        this.$toasted.global.danger('Erreur lors du téléchargement des fichiers.')
      }
    },
    async handleExportAll () {
      try {
        // this.$toasted.global.success('Vous allez recevoir une notification quand votre fichier sera prêt')
        const documentName = 'Liste des advs'
        const service = this.service
        let filtersCopy = { ...this.filters }
        let keysToRemove = []
        for (let key in filtersCopy) {
          if (filtersCopy[key] === null || (Array.isArray(filtersCopy[key]) && filtersCopy[key].length === 0)) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => delete filtersCopy[key])
        for (let property in filtersCopy) {
          if (typeof filtersCopy[property] === 'object' && !Array.isArray(filtersCopy[property])) {
            for (let key in filtersCopy[property]) {
              if (key !== 'pk') {
                delete filtersCopy[property][key]
              }
            }
          } else if (Array.isArray(filtersCopy[property])) {
            let pkList = filtersCopy[property]
              .filter(item => typeof item === 'object' && item.hasOwnProperty('pk'))
              .map(item => item.pk)
            if (pkList.length === 0) {
              let validList = filtersCopy[property].filter(item => item !== null && item !== undefined && item !== '')
              if (validList.length > 0) {
                filtersCopy[property] = validList
              } else {
                delete filtersCopy[property]
              }
            } else {
              filtersCopy[property] = pkList
            }
          }
        }
        const statusFilters = {
          ...filtersCopy
        }
        this.downloadAdvs(this.selectedItems, documentName, service, statusFilters)
      } catch (error) {
        this.$toasted.global.danger('Erreur lors de l\'envoie des advs !')
      }
    },

    onSearchByContactName () {
      this.filters.contact = null
      this.filters.searchable_contact = this.searchInput
      this.updateSimpleFilter()
      this.$refs.contactMultiselect.deactivate()
    },

    onContactSelection () {
      this.filters.searchable_contact = null
      this.updateSimpleFilter()
    },

    onSearchChange (query) {
      if (this.searchInputTimeout) {
        clearTimeout(this.searchInputTimeout)
      }
      this.searchInputTimeout = setTimeout(async () => {
        await this.getContacts(query)
        this.searchInput = query
        this.searchInputTimeout = null
      }, 300)
    },

    toggleAllPrograms () {
      if (this.filters.programs && this.filters.programs.length === this.programsFilter.length) {
        // Si tous les programmes sont déjà sélectionnés, les désélectionner tous
        this.filters.programs = []
      } else {
        // Ajouter tous les programmes à la sélection existante (sans doublons)
        const currentSelection = this.filters.programs || []
        const allPrograms = [...this.programsFilter]
        // Fusionner les deux listes en évitant les doublons
        const merged = [...currentSelection]
        allPrograms.forEach(program => {
          if (!merged.find(p => p.pk === program.pk)) {
            merged.push(program)
          }
        })
        this.filters.programs = merged
      }
      this.updateSimpleFilter()
    }
  }
}
</script>

<style lang="sass">
.export_btn_style_adv
  width: 217px
  min-height: 100%
  position: relative

.btn_export_adv
  background-color: #fff
  color: #7a7a7a
  width: 100%
  min-height: 100%
  font-size: 1.2rem
  display: flex
  align-items: center
  padding: 16px 56px

.container_select_multiple_adv
  width: 100%

.select_multiple_adv
  text-decoration: none !important
  padding: 13px
  width: 100%
  color: white

.select_multiple_adv:hover
  color: white
  text-decoration: none

.searchResult-list-item--viewMore
  color: #209cee
  font-size: 1.05rem

.searchResult-list-item--viewMore:hover
  color: #209cee
  font-size: 1.05rem

</style>
